namespace appn.designerv2 {
    export const BASE_COMPONENT_TYPE = {
        GROUP: 'group'
    }
    
    export const PROPERTY_TYPE = { 
        TEXT: 'text', 
        TEXT_MULTI: 'textMulti',
        UI_TEXT: 'uiText',
        BOOL: 'bool',
        ENUM: 'enum',
        NUMERIC: 'numeric',
        COLOR: 'colorSelector',
        FONT_STYLE: 'fontStyle',
        LABEL_COPY: 'labelCopy',
        FILE_UPLOAD: 'fileUpload',
        GROUP: 'group'
    }

    export const BASE_PROPERTIES: { [key: string]: IPropertyDefinition } = {
        label: { name: T('Title'), type: 'uiText', tooltip: T('The title text for this element'), defaultValue: { eng: 'Sample Title'}, sector: 'basic' },
        labelStyle: { name: T('Title Style'), type: 'fontStyle', tooltip: T('Style for the title text'), defaultValue: { color: '#333333', decor: 'Normal', size: 'Medium'}, sector: 'basic' },
        hint: { name: T('Hint'), type: 'uiText', tooltip: T('Hint text displayed below the title'), defaultValue: { eng: ''}, sector: 'basic' },
        hintStyle: { name: T('Hint Style'), type: 'fontStyle', tooltip: T('Style for the hint text'), defaultValue: { color: '#777777', decor: 'Italic', size: 'Small'}, sector: 'basic' }
    };

    export const EVENTS = {
        STATE_CHANGED: 'state:changed',
        COMPONENT_SELECTED: 'component:selected',
        PROPERTY_UPDATED: 'property:updated',
        DATA_REFRESH_REQUESTED: 'data:refresh_requested',
        DATA_FETCH_SUCCESS: 'data:fetch_success',
        ERROR_OCCURRED: 'error.occurred'
    };
    
    export enum FormulaType {
        fieldsOnly = 'fieldsOnly',
        template = 'template',
        uiText = 'uitext',
        normal = 'text'
    }

    export enum FormulaMode {
        xml = 'xml',
        json = 'json',
        url = 'url',
        email = 'email'
    }
    
    export interface IPropertyUpdatedPayload { 
        componentId: string;
        propName: string;
        propValue: any;
        oldValue: any;
        propPath?: string;
    }

    export interface IDesignerArgs {
        name: string;
        canSave: boolean;
        iconPaths: { [key: string]: string };
        dataFetchUrl: string;
        designerJSONFieldName: string;
        // Catch-all for designer-specific properties
        [key: string]: any;
    }

    export interface IGridPosition {
        x: number;
        y: number;
        w: number;
        h: number;
    }

    export interface ITranslationText {
        [key: string]: string;
    }

    export interface IFontStyle {
        color?: string;
        decor?: 'Normal' | 'Bold' | 'Italic';
        size?: 'Small' | 'Medium' | 'Large';
    }

    export interface IHeading {
        id: string;
        label: string;
        icon: string;
    }

    export interface IComponent {
        id: string;
        type: string;
        gridPosition?: IGridPosition | null;
        // Basic properties
        name?: string;
        label?: ITranslationText;
        labelStyle?: IFontStyle;
        hint?: ITranslationText;
        hintStyle?: IFontStyle;
        bgColor?: string;
        // Children for group components
        children?: IComponent[];
        // Catch-all for component-specific properties
        [key: string]: any;
    }

    export interface IDesignMetadata {
        activeLanguages: string[];
        // Allow arbitrary metadata keys; these will be output in the designer JSON
        [key: string]: any;
    }

    export interface IValidationError {
        componentId: string;
        propName: string;
        errors: string[];
    }

    export interface IDesign {
        title: string;
        metadata: IDesignMetadata[];
        components: IComponent[];
    }

    export interface IPropertyDefinition {
        name: string;
        type: string;
        tooltip: string;
        defaultValue: any;
        formulaType?: FormulaType,
        formulaMode?: FormulaMode | FormulaMode[],
        options?: (string | number)[] | { text: string, value: any }[];
        limit?: IPropertyLimit;
        sector: string;
        container?: string;
        path?: string; // For data properties path inside dataset
        config?: Record<string, any>;
        visibleWhen?: (component: IComponent) => boolean;
    }

    export interface IPropertyLimit {
        required?: boolean;
        unique?: boolean;
        alphanumeric?: boolean;
        numeric?: boolean;
        maxLength?: number;
        minLength?: number;
        max?: number;
        min?: number;
        pattern?: string;
        custom?: ((oldValue: any, newValue: any, validationErrors: string[]) => void)[];
        canBeExpanded?: string;
    }
    
    export interface IComponentDefinition<T extends IPropertyDefinition = IPropertyDefinition> {
        title: string;
        description: string;
        icon: string;
        category: string;
        defaultSize?: IGridPosition;
        properties: { [key: string]: T };
        sortProperties?: () => { [key: string]: T };
        render?: (element: JQuery, component: IComponent) => void;
        toggleEditors?: (component: IComponent, editors: { [propName: string]: editors.EditorBase }) => void;
    }

    /**
     * A captured copy of a component's root property value (e.g. "dataQuery0"). restore() writes the value
     * back synchronously and has no other side effects, so the caller owns any ordering - in particular,
     * guard the path first if other editors must not react to (and overwrite) the restored value.
     */
    export interface IRootSnapshot {
        /** The captured value, for callers that need to inspect what was loaded. */
        readonly value: any;
        /** Restore the captured value. */
        restore(): void;
    }

    /**
     * Exclusive write ownership of a component property path while held. Other editors' writes to that
     * path or anything beneath it are dropped, so a read-only path (e.g. a restricted dataset's query) is kept intact.
     */
    export interface IPathGuard {
        release(): void;
    }
}
